<template>
  <v-app>
    <Loading v-show="loading" />
    <v-container v-show="!loading">
      <v-row dense>
        <v-col cols="12">
          <v-container v-if="isValidCondition">
            <v-row justify="space-between">
              <v-col cols="1">
                <v-btn icon @click="$router.go(-1)">
                  <v-icon color="blue">
                    mdi-arrow-left
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="text-center">
              <v-col>
                <h3>
                  修了証発行
                </h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-center" cols="12" style="white-space: pre-wrap;" v-text="'修了証発行の準備が完了しました。\n以下の入力フォームに修了証に記載する氏名を入力し、\n『修了証を表示・ダウンロードする』ボタンを押してください。\n画面下部にプレビュー画像が表示されると同時に、PDFのダウンロードが開始します。'" />
            </v-row>
            <v-row>
              <v-col class="text-center" cols="12" style="white-space: pre-wrap;" v-text="'講座名：' + courseSyllabus.courseTitle" />
            </v-row>
            <v-row justify="center">
              <v-col cols="8">
                <v-text-field
                  v-model="inputCertificatedUserName"
                  label="Input Your Name"
                  outlined
                  clearable
                />
              </v-col>
            </v-row>
            <v-row class="text-center">
              <v-col>
                <v-btn color="red" dark @click="print(formattedUserName(), inputCertificatedUserName, certificationData, certificationCheck)">
                  修了証を表示・ダウンロードする
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <canvas id="target" width="2339" height="1654" style="width:100%" />
              </v-col>
            </v-row>
          </v-container>
          <v-container v-else class="text-center">
            不正な画面遷移を検出しました。
          </v-container>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios'
import store from '@/store/store'
import repository from '@/store/repository'
import Loading from '@/components/common/Loading'
import handleApiError from '@/plugins/handleApiError'
import { jsPDF } from 'jspdf'

const COURSE_NAME_X = 560
const COURSE_NAME_Y = 855
const COURSE_NAME_MAX_WIDTH = 1680
const COURSE_NAME_MAX_LINES = 2
const COURSE_NAME_MIN_FONT_SIZE = 32
const COURSE_CERT_ID_X = 560
const COURSE_CERT_ID_Y_GAP = 65
const BREAK_AFTER_CHARS = new Set([' ', '\u3000', '：', ':', '、', '。', '，', '．', '・'])
const BREAK_BEFORE_CHARS = new Set(['―', '-', '（', '(', '「', '『', '【'])

function setCanvasFont (ctx, sizePt, fontFamily = 'Arial') {
  ctx.font = `${sizePt}pt ${fontFamily}`
}

function measureTextWidth (ctx, text) {
  return ctx.measureText(text).width
}

function getBreakIndices (text) {
  const indices = new Set([0, text.length])

  for (let i = 0; i < text.length; i++) {
    if (BREAK_AFTER_CHARS.has(text[i])) {
      indices.add(i + 1)
    }
    if (i < text.length - 1 && BREAK_BEFORE_CHARS.has(text[i + 1])) {
      indices.add(i + 1)
    }
  }

  return [...indices].sort((a, b) => a - b)
}

function trimLineEdges (text) {
  return text.replace(/^[\s\u3000]+|[\s\u3000]+$/g, '')
}

function findLongestFittingEnd (ctx, text, start, maxWidth, breakIndices) {
  let bestEnd = -1

  for (const end of breakIndices) {
    if (end <= start) continue
    if (measureTextWidth(ctx, text.slice(start, end)) <= maxWidth) {
      bestEnd = end
    } else {
      break
    }
  }

  return bestEnd
}

function findCharBreakEnd (ctx, text, start, maxWidth) {
  let end = start + 1
  while (end <= text.length && measureTextWidth(ctx, text.slice(start, end)) <= maxWidth) {
    end++
  }
  return Math.max(start + 1, end - 1)
}

function wrapTextLines (ctx, text, maxWidth, maxLines) {
  const breakIndices = getBreakIndices(text)
  const lines = []
  let start = 0

  while (start < text.length && lines.length < maxLines) {
    let end = findLongestFittingEnd(ctx, text, start, maxWidth, breakIndices)

    if (end === -1) {
      end = findCharBreakEnd(ctx, text, start, maxWidth)
    }

    lines.push(trimLineEdges(text.slice(start, end)))
    start = end
    while (start < text.length && /[\s\u3000]/.test(text[start])) {
      start++
    }
  }

  if (start < text.length && lines.length > 0) {
    const lastIndex = lines.length - 1
    let lastLine = trimLineEdges(lines[lastIndex] + text.slice(start))
    while (measureTextWidth(ctx, lastLine + '…') > maxWidth && lastLine.length > 1) {
      lastLine = lastLine.slice(0, -1)
    }
    lines[lastIndex] = lastLine + '…'
  }

  return lines.filter((line) => line.length > 0)
}

function drawFittedText (ctx, text, x, y, maxWidth, options = {}) {
  const maxLines = options.maxLines || 1
  const fontSize = options.fontSize || 40
  const minFontSize = options.minFontSize || COURSE_NAME_MIN_FONT_SIZE
  const lineHeightRatio = options.lineHeightRatio || 1.2
  const fontFamily = options.fontFamily || 'Arial'

  for (let size = fontSize; size >= minFontSize; size--) {
    setCanvasFont(ctx, size, fontFamily)
    if (measureTextWidth(ctx, text) <= maxWidth) {
      ctx.fillText(text, x, y)
      return { lastLineY: y, lineCount: 1 }
    }
  }

  setCanvasFont(ctx, minFontSize, fontFamily)
  const lines = wrapTextLines(ctx, text, maxWidth, maxLines)

  if (lines.length <= 1) {
    ctx.fillText(text, x, y)
    return { lastLineY: y, lineCount: 1 }
  }

  const lineHeight = minFontSize * lineHeightRatio
  const startY = y - ((lines.length - 1) * lineHeight) / 2

  lines.forEach((line, index) => {
    ctx.fillText(line, x, startY + index * lineHeight)
  })

  return { lastLineY: startY + (lines.length - 1) * lineHeight, lineCount: lines.length }
}

export default {
  components: {
    Loading
  },
  data () {
    return {
      loading: false,
      units: require(`@/assets/courseData/courseUnits/${this.$route.params.courseId}.json`),
      courseSyllabus: require(`@/assets/courseData/courseSyllabuses/${this.$route.params.courseId}.json`),
      currentCourse: null,
      certificatedUserName: '',
      certificationData: {
        courseName: '',
        systemName: '',
        courseCertId: '',
        courseCertId2: '',
        instructor: '',
        instructorAffiliation: '',
        courseSign: ''
      },
      certificationCheck: {
        pjmUserId: '',
        courseId: '',
        coursePassDate: '',
        certManagementId: ''
      },
      x: 221034234,
      y: 112342509,
      z: 987245243,
      t: null
    }
  },
  computed: {
    inputCertificatedUserName: {
      get () {
        return this.certificatedUserName
      },
      set (inputName) {
        this.certificatedUserName = inputName
        return this.certificatedUserName
      }
    },

    isValidCondition: function () {
      // certManagementId が JMOOC... となっているなら true
      const managementId = this.certificationCheck.certManagementId
      return managementId.startsWith('JMOOC')
    }
  },
  async mounted () {
    // 各種定数・変数宣言
    const courseId = this.$route.params.courseId
    const courseData = require('@/assets/courseData/courseCards.json')
    this.currentCourse = courseData.find((course) => course.systemName === courseId)
    this.certificationData = {
      courseName: this.currentCourse.courseName,
      systemName: this.currentCourse.systemName,
      courseCertId: this.currentCourse.courseCertId,
      courseCertId2: this.currentCourse.courseCertId2,
      instructor: this.currentCourse.instructor,
      instructorAffiliation: this.currentCourse.instructorAffiliation,
      coursePhoto: this.currentCourse.coursePhoto,
      courseSign: this.currentCourse.courseSign
    }
    // 修了証合否確認および発行記録書き込み
    this.loading = true
    await this.pjmCertCheck(this.currentCourse)
    this.loading = false
  },
  methods: {
    print: (displayUserName, inputName, certificationData, certificationCheck) => {
      // canvas生成
      const canvas = document.getElementById('target')
      const ctx = canvas.getContext('2d')

      // 背景画像
      const baseImage = new Image()
      baseImage.src = require('@/assets/certification/certification_background.png')

      // 講座アイコン
      const unitCardImage = new Image()
      unitCardImage.src = require('@/assets/courseData/courseCardImg/' + certificationData.coursePhoto)

      // 講師サイン
      const signImage = new Image()
      signImage.src = require('@/assets/courseData/courseSign/' + certificationData.courseSign)

      baseImage.onload = () => {
        ctx.drawImage(baseImage, 0, 0, 2339, 1654)

        window.setTimeout(function () {
          // コースカード画像
          // baseImage.addEventListener('load', function () {
          ctx.drawImage(unitCardImage, 170, 780, 328, 184)
          // }, false)
          // サイン画像（サイズは250px*1500pxの倍数に固定すること）
          ctx.drawImage(signImage, 170, 1200, 750, 125)

          // ユーザID
          ctx.font = '32pt Arial'
          if (!displayUserName.match('googleuser')) {
            ctx.fillText(displayUserName, 160, 465)
          // ctx.fillText('JM0000001', 170, 465)
          }

          // 氏名
          ctx.font = '64pt Arial'
          ctx.fillText(inputName, 170, 575)

          // 講座名（長い講座名はフォント縮小または改行、最小32pt＝講座IDと同サイズ）
          const courseNameLayout = drawFittedText(ctx, certificationData.courseName, COURSE_NAME_X, COURSE_NAME_Y, COURSE_NAME_MAX_WIDTH, {
            fontSize: 40,
            minFontSize: COURSE_NAME_MIN_FONT_SIZE,
            maxLines: COURSE_NAME_MAX_LINES,
            lineHeightRatio: 1.2
          })

          // 講座ID（講座名が改行された場合は下にずらす）
          ctx.font = '32pt Arial'
          ctx.fillText(certificationData.courseCertId, COURSE_CERT_ID_X, courseNameLayout.lastLineY + COURSE_CERT_ID_Y_GAP)

          // 修了日
          ctx.font = '32pt Arial'
          const date = new Date(certificationCheck.coursePassDate * 1000)
          ctx.fillText('修了日：' + date.getFullYear() + '年' + (date.getMonth() + 1) + '月' + date.getDate() + '日', 170, 1100)

          // 講師名
          ctx.font = '32pt Arial'
          ctx.fillText('講師　' + certificationData.instructor, 170, 1400)

          // 講師所属
          ctx.font = '24pt Arial'
          if (certificationData.instructorAffiliation !== null) {
            ctx.fillText(certificationData.instructorAffiliation, 170, 1450)
          }

          // 修了証ID
          ctx.font = '24pt Arial'
          ctx.fillText(certificationCheck.certManagementId, 1587, 1430)
        }, 500)

        window.setTimeout(function () {
          // PDFを作成する
          const imgData = canvas.toDataURL('image/jpeg', 1.0) // canvas要素をjpegをBase64形式に変換する
          const canvasImageWidth = canvas.width
          const canvasImageHeight = canvas.height

          // eslint-disable-next-line new-cap
          const doc = new jsPDF('l', 'pt', [canvasImageWidth, canvasImageHeight])
          doc.addImage(imgData, 'JPG', 0, 0, canvasImageWidth, canvasImageHeight)
          doc.save('Certificate_' + certificationCheck.certManagementId + '.pdf')
        }, 1000)
      }
    },
    async pjmCertCheck (currentCourse) {
      const reqUrl = process.env.VUE_APP_CERT_ISSUANCE_LOG
      const reqBody = {
        pjmUserId: String(repository.getPjmUserId()),
        cognitoUsername: store.state.username,
        cognitoIdToken: repository.getIdToken(),
        courseId: currentCourse.courseId
      }
      const header = {
        headers: {
          'x-api-key': process.env.VUE_APP_DB_API_KEY,
          'Content-Type': 'application/json',
          Authorization: repository.getAccessToken()
        }
      }

      const res = await axios.post(reqUrl, reqBody, header).catch(handleApiError)
      this.certificationCheck = res.data
    },
    formattedUserName: function () {
      const name = store.state.username
      if (!name) return ''

      if (name.includes('google')) {
        let seed = Number(name.slice(-9))
        for (let i = 0; i < 5; i++) {
          seed = this.xorShift(seed)
        }
        return `googleuser${String(seed).slice(-9)}`
      }

      return name
    },
    xorShift: function (seed) {
      this.t = this.x ^ (this.x << 11)
      this.x = this.y
      this.y = this.z
      this.z = seed
      seed = (seed ^ (seed >>> 19)) ^ (this.t ^ (this.t >>> 8))
      return seed
    }
  }
}
</script>
